import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import BubbleAlert from './bubbleAlert';
import Snackbar from './snackbar';

type AlertType = 'error' | 'success' | 'info';
type AlertItem = { id: string; message: string; type?: AlertType; durationMs?: number };

type AlertContextValue = {
  showAlert: (message: string, type?: AlertType, durationMs?: number) => void;
  showSnack?: (message: string, actionLabel?: string, onAction?: () => void) => void;
};

const AlertContext = createContext<AlertContextValue>({
    showAlert: () => { },
});

export const useAlert = (): AlertContextValue => {
  const ctx = useContext(AlertContext);
  if (!ctx) throw new Error('useAlert must be used within AlertProvider');
  return ctx;
};

export const AlertProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [alerts, setAlerts] = useState<AlertItem[]>([]);
  const [snack, setSnack] = useState<{ id: string; message: string; actionLabel?: string; onAction?: () => void } | null>(null);

  const removeAlert = useCallback((id: string) => {
    setAlerts(prev => prev.filter(a => a.id !== id));
  }, []);


    const showAlert = useCallback((message: string, type: AlertType = 'error', durationMs = 5000) => {
    const id = (typeof crypto !== 'undefined' && (crypto as any).randomUUID) ? (crypto as any).randomUUID() : Math.random().toString(36).slice(2);
    const item: AlertItem = { id, message, type, durationMs };
    setAlerts(prev => [...prev, item]);
  }, []);

  const showSnack = useCallback((message: string, actionLabel?: string, onAction?: () => void) => {
    const id = (typeof crypto !== 'undefined' && (crypto as any).randomUUID) ? (crypto as any).randomUUID() : Math.random().toString(36).slice(2);
    setSnack({ id, message, actionLabel, onAction });
  }, []);

  return (
    <AlertContext.Provider value={{ showAlert, showSnack }}>
      {children}
      <div aria-live="polite">
        {alerts.map(a => (
          <BubbleAlert
            key={a.id}
            message={a.message}
            type={a.type}
            durationMs={a.durationMs}
            onClose={() => removeAlert(a.id)}
          />
        ))}
        {snack ? <Snackbar message={snack.message} actionLabel={snack.actionLabel} onAction={snack.onAction} onClose={() => setSnack(null)} /> : null}
      </div>
    </AlertContext.Provider>
  );
};
